<template>
  <div>
    <!-- ── Assessment dialog ── -->
    <v-dialog v-model="dialog" eager persistent scrollable fullscreen>
      <v-card
        class="d-flex flex-column"
        style="height: 100vh; overflow: hidden"
      >
        <!-- Top bar -->
        <div
          class="assessment-topbar d-flex align-center justify-space-between px-6 py-3"
        >
          <div>
            <div class="text-subtitle-1 font-weight-bold">
              {{ data && data.title }}
            </div>
            <div class="text-caption text-grey">
              Question {{ currentIndex + 1 }} of {{ questions.length }}
            </div>
          </div>
          <div class="d-flex align-center gap-3">
            <div
              class="timer-chip d-flex align-center gap-1 px-3 py-2 rounded-lg"
              :class="
                timerWarning ? 'timer-chip--warning' : 'timer-chip--normal'
              "
            >
              <v-icon size="16">mdi-clock-outline</v-icon>
              <span class="font-weight-bold text-body-2">{{
                formattedTime
              }}</span>
            </div>
            <v-btn
              color="#10b981"
              variant="flat"
              class="text-white"
              prepend-icon="mdi-check-circle-outline"
              :disabled="
                answeredCount < questions.length || questions.length === 0
              "
              @click="submit"
            >
              Submit
            </v-btn>
          </div>
        </div>

        <v-divider />

        <!-- 3-column layout: Prev | Question | Next -->
        <div
          class="assessment-body d-flex align-center flex-grow-1 px-4 py-6"
          style="overflow: hidden"
        >
          <!-- Left nav -->
          <div class="nav-side d-flex align-center justify-center">
            <v-btn
              icon
              variant="tonal"
              size="large"
              color="#10b981"
              :disabled="currentIndex === 0"
              @click="prevCard"
            >
              <v-icon size="28">mdi-chevron-left</v-icon>
            </v-btn>
          </div>

          <!-- Question card (center) -->
          <div
            class="question-center flex-grow-1 d-flex align-center justify-center px-4"
            style="overflow-y: auto"
          >
            <transition :name="transitionName" mode="out-in">
              <v-card
                :key="currentIndex"
                variant="outlined"
                rounded="lg"
                class="question-card"
                style="max-width: 700px; width: 100%"
              >
                <!-- Question header -->
                <div class="px-6 pt-5 pb-3">
                  <v-chip
                    size="small"
                    color="#10b981"
                    variant="tonal"
                    class="mb-3"
                  >
                    Question {{ currentIndex + 1 }} / {{ questions.length }}
                  </v-chip>
                  <div
                    class="text-body-1 font-weight-medium"
                    style="white-space: pre-wrap; line-height: 1.7"
                  >
                    {{ currentCard && currentCard.question }}
                  </div>
                </div>

                <v-divider class="mx-6" />

                <!-- Options -->
                <div class="px-6 py-4" v-if="currentCard">
                  <v-radio-group
                    v-model="currentCard.my_answer"
                    hide-details
                    color="#10b981"
                  >
                    <v-radio
                      v-for="opt in currentCard.options"
                      :key="opt.id"
                      :label="opt.option_desc"
                      :value="opt.option_no"
                      color="#10b981"
                      class="option-radio mb-1"
                    />
                  </v-radio-group>
                </div>

                <v-divider />

                <!-- Pagination dots -->
                <div
                  class="pag-row d-flex align-center justify-center flex-wrap py-3 px-4 gap-1"
                >
                  <button
                    v-for="(q, idx) in questions"
                    :key="idx"
                    class="pag-dot"
                    :class="{
                      'pag-dot--current': idx === currentIndex,
                      'pag-dot--answered':
                        idx !== currentIndex && q.my_answer != null,
                      'pag-dot--empty':
                        idx !== currentIndex && q.my_answer == null,
                    }"
                    :title="`Question ${idx + 1}${
                      q.my_answer != null ? ' (answered)' : ''
                    }`"
                    @click="goToQuestion(idx)"
                  />
                </div>
              </v-card>
            </transition>
          </div>

          <!-- Right nav -->
          <div class="nav-side d-flex align-center justify-center">
            <v-btn
              icon
              variant="tonal"
              size="large"
              color="#10b981"
              :disabled="currentIndex === questions.length - 1"
              @click="nextCard"
            >
              <v-icon size="28">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
        <!-- end assessment-body -->
      </v-card>
    </v-dialog>

    <!-- ── Multi-screen block dialog (before assessment starts) ── -->
    <v-dialog
      v-model="showMultiScreenBlockDialog"
      eager
      persistent
      max-width="440"
    >
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center pa-4 bg-orange-lighten-5">
          <v-icon color="orange-darken-2" class="mr-2"
            >mdi-monitor-multiple</v-icon
          >
          <span class="text-subtitle-1 font-weight-bold"
            >Multiple Screens Detected</span
          >
        </v-card-title>
        <v-card-text class="pa-4 text-body-2 text-grey-darken-2">
          A second monitor has been detected. Please disconnect it before
          proceeding with the assessment.
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" color="grey-darken-1" @click="closeD">
            Cancel
          </v-btn>
          <v-btn
            color="#10b981"
            variant="flat"
            class="text-white"
            @click="retryScreenCheck"
          >
            I've Disconnected It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Multi-screen auto-submit dialog (during assessment) ── -->
    <v-dialog v-model="showMultiScreenDialog" eager persistent max-width="440">
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center pa-4 bg-red-lighten-5">
          <v-icon color="red-darken-2" class="mr-2"
            >mdi-monitor-multiple</v-icon
          >
          <span class="text-subtitle-1 font-weight-bold"
            >Assessment Auto-Submitted</span
          >
        </v-card-title>
        <v-card-text class="pa-4 text-body-2 text-grey-darken-2">
          <p class="mb-2">
            Multiple monitors were detected. Your assessment has been
            automatically submitted and will close in
            <strong>{{ multiScreenCountdown }}</strong> second(s).
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ── Start / Instructions dialog ── -->
    <v-dialog
      v-model="showWarningDialog"
      eager
      persistent
      scrollable
      max-width="560"
    >
      <v-card rounded="lg">
        <v-card-text class="pa-8 text-center">
          <div class="instructions-icon mx-auto mb-4">
            <v-icon size="44" color="#10b981">mdi-seal</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mb-1">
            Skills Assessment Test
          </div>
          <div class="text-body-2 text-grey-darken-1 mb-6">
            Validate your technical knowledge to get matched with the best
            internship opportunities.
          </div>

          <v-card
            variant="tonal"
            color="success"
            rounded="lg"
            class="pa-4 text-left"
          >
            <div class="d-flex align-center gap-2 mb-3">
              <v-icon color="#10b981"
                >mdi-book-open-blank-variant-outline</v-icon
              >
              <span class="text-subtitle-2 font-weight-bold">Instructions</span>
            </div>
            <div
              v-for="(instruction, i) in instructions"
              :key="i"
              class="d-flex align-start gap-2 mb-2"
            >
              <v-icon color="#10b981" size="16" class="mt-1 flex-shrink-0">
                mdi-checkbox-marked-circle-outline
              </v-icon>
              <span class="text-body-2 text-grey-darken-2">{{
                instruction
              }}</span>
            </div>
          </v-card>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" color="grey-darken-1" @click="closeD">
            Cancel
          </v-btn>
          <v-btn
            color="#10b981"
            variant="flat"
            class="text-white"
            prepend-icon="mdi-play"
            :disabled="btnLoading || isMultiscreen"
            :loading="btnLoading"
            @click="proceed"
          >
            Start Assessment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Submit confirmation dialog ── -->
    <v-dialog
      v-model="showAnswersDialog"
      eager
      persistent
      scrollable
      max-width="520"
    >
      <v-card rounded="lg">
        <v-card-title
          class="d-flex align-center pa-4"
          :class="
            isVoluntarySubmit ? 'bg-orange-lighten-5' : 'bg-red-lighten-5'
          "
        >
          <v-icon
            class="mr-2"
            :color="isVoluntarySubmit ? 'orange-darken-2' : 'red-darken-2'"
          >
            {{
              isVoluntarySubmit
                ? "mdi-help-circle-outline"
                : "mdi-clock-alert-outline"
            }}
          </v-icon>
          <span class="text-subtitle-1 font-weight-bold">
            {{ isVoluntarySubmit ? "Submit Assessment?" : "Time's Up!" }}
          </span>
        </v-card-title>

        <v-card-text class="pa-4">
          <p class="text-body-2 text-grey-darken-2 mb-4">
            {{
              isVoluntarySubmit
                ? "Review your answers below. Green = answered, grey = skipped."
                : "Time is up! Your answers will be saved as-is. Click Submit to proceed."
            }}
          </p>

          <!-- Answer grid -->
          <div class="answer-grid">
            <button
              v-for="(q, idx) in questions"
              :key="idx"
              class="ans-cell"
              :class="
                q.my_answer != null ? 'ans-cell--answered' : 'ans-cell--empty'
              "
              @click="jumpAndClose(idx)"
              :title="`Question ${idx + 1}`"
            >
              {{ idx + 1 }}
            </button>
          </div>

          <div class="d-flex align-center gap-3 mt-4">
            <div class="d-flex align-center gap-1">
              <span class="ans-legend ans-legend--answered" />
              <span class="text-caption">Answered ({{ answeredCount }})</span>
            </div>
            <div class="d-flex align-center gap-1">
              <span class="ans-legend ans-legend--empty" />
              <span class="text-caption"
                >Skipped ({{ questions.length - answeredCount }})</span
              >
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            v-if="isVoluntarySubmit"
            variant="text"
            color="grey-darken-1"
            :disabled="confirmBtnLoading"
            @click="cancelConfirm"
          >
            Go Back
          </v-btn>
          <v-btn
            color="#10b981"
            variant="flat"
            class="text-white"
            prepend-icon="mdi-check-circle"
            :disabled="confirmBtnLoading"
            :loading="confirmBtnLoading"
            @click="confirm"
          >
            {{ isVoluntarySubmit ? "Confirm & Submit" : "Submit Now" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Tab-switch warning dialog ── -->
    <v-dialog
      v-model="showDialogForSwitching"
      eager
      persistent
      scrollable
      max-width="440"
    >
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center pa-4 bg-red-lighten-5">
          <v-icon color="red-darken-2" class="mr-2"
            >mdi-alert-circle-outline</v-icon
          >
          <span class="text-subtitle-1 font-weight-bold"
            >Assessment Auto-Submitted</span
          >
        </v-card-title>
        <v-card-text class="pa-4 text-body-2 text-grey-darken-2">
          The system detected you have switched tabs/windows. This assessment
          has been automatically submitted.
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="#10b981"
            variant="flat"
            class="text-white"
            @click="reload"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";

export default {
  props: {
    data: Object,
    action: String,
  },

  data() {
    return {
      showWarningDialog: false,
      showDialogForSwitching: false,
      showMultiScreenDialog: false,
      showMultiScreenBlockDialog: false,
      multiScreenCountdown: 3,
      multiScreenCountdownInterval: null,
      dialog: false,
      btnLoading: false,
      isWarningAccepted: false,
      questions: [],
      duration: null,
      timeRemaining: null,
      timerInterval: null,
      currentIndex: 0,
      transitionDirection: "right",
      showAnswersDialog: false,
      isTimerRunning: false,
      isVoluntarySubmit: false,
      confirmBtnLoading: false,
      isMultiscreen: false,
      multiScreenCheckActive: false,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "System Message",
        message: "",
        top: 10,
      },
      windowBlurTimeout: null,
    };
  },

  computed: {
    formattedTime() {
      if (this.timeRemaining == null) return "0:00";
      const minutes = Math.floor(this.timeRemaining / 60);
      const seconds = this.timeRemaining % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    },

    timerWarning() {
      return (
        this.timeRemaining != null &&
        this.timeRemaining <= 60 &&
        this.timeRemaining > 0
      );
    },

    currentCard() {
      return this.questions[this.currentIndex];
    },

    transitionName() {
      return `slide-${this.transitionDirection}`;
    },

    answeredCount() {
      return this.questions.filter((q) => q.my_answer != null).length;
    },

    instructions() {
      return [
        `You have ${this.duration} minute(s) to answer all the questions.`,
        "Questions are scrambled accordingly.",
        "Once done, click the Submit button to submit your answers.",
        "Once you switch tabs or windows, this assessment will be automatically submitted.",
        "Connecting multiple monitors will automatically submit and close the assessment.",
      ];
    },
  },

  watch: {
    data: {
      handler(data) {
        if (!data) return;
        this.dialog = true;
        this.getQuestions(data.id);
        this.currentIndex = 0;
        this.showWarningDialog = true;
        this.duration = data.time_limit;
        this.timeRemaining = 0;
        this.isWarningAccepted = false;
        this.multiScreenCheckActive = true;

        if ("screen" in window && window.screen.addEventListener) {
          window.screen.removeEventListener("change", this.onScreenChange);
          window.screen.addEventListener("change", this.onScreenChange);
        }

        // Initial check after dialog mounts — catches already-connected monitors
        this.$nextTick(() => {
          this.checkMultiScreen();
        });
      },
      deep: true,
    },

    dialog: {
      handler(val) {
        if (!val) {
          document.removeEventListener(
            "visibilitychange",
            this.handleVisibilityChange,
          );
          this.stopMultiScreenCheck();
        }
      },
    },

    isTimerRunning: {
      handler(val) {
        if (val) {
          document.addEventListener(
            "visibilitychange",
            this.handleVisibilityChange,
          );
          window.addEventListener("blur", this.handleWindowBlur);
          window.addEventListener("focus", this.handleWindowFocus);
        }
      },
    },

    timeRemaining: {
      handler(val) {
        if (
          this.dialog &&
          !this.showWarningDialog &&
          !this.isTimerRunning &&
          val === 0
        ) {
          this.isVoluntarySubmit = false;
          this.showAnswersDialog = true;
        }
      },
    },
  },

  beforeUnmount() {
    this.stopMultiScreenCheck();
    clearInterval(this.timerInterval);
    clearInterval(this.multiScreenCountdownInterval);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    if ("screen" in window && window.screen.removeEventListener) {
      window.screen.removeEventListener("change", this.onScreenChange);
    }
    window.removeEventListener("blur", this.handleWindowBlur);
    window.removeEventListener("focus", this.handleWindowFocus);
    clearTimeout(this.windowBlurTimeout);
  },

  methods: {
    getQuestions(skillAssessmentId) {
      this.axiosCall(
        "/skill-assessement/getQuestions/" + skillAssessmentId,
        "GET",
      ).then((res) => {
        if (res) {
          this.questions = this.shuffleArray(res.data);
        }
      });
    },

    handleWindowFocus() {
      clearTimeout(this.windowBlurTimeout);
    },

    handleWindowBlur() {
      // small delay so we don't fire on things like opening the
      // browser's own file/print dialogs, or a momentary devtools toggle
      this.windowBlurTimeout = setTimeout(() => {
        if (!document.hasFocus() && this.isTimerRunning) {
          this.handleVisibilityChange();
        }
      }, 300);
    },

    onScreenChange() {
      if (!this.dialog || !this.multiScreenCheckActive) return;
      this.checkMultiScreen();
    },

    checkMultiScreen() {
      if (!this.multiScreenCheckActive || !this.dialog) return;

      if (window.screen.isExtended) {
        this.isMultiscreen = true;

        if (this.isWarningAccepted) {
          // Assessment already running — auto-submit and countdown close
          this.saveOnAutoSubmission();
          this.multiScreenCountdown = 3;
          this.showMultiScreenDialog = true;

          clearInterval(this.multiScreenCountdownInterval);
          this.multiScreenCountdownInterval = setInterval(() => {
            this.multiScreenCountdown--;
            if (this.multiScreenCountdown <= 0) {
              clearInterval(this.multiScreenCountdownInterval);
              this.showMultiScreenDialog = false;
              this.stopMultiScreenCheck();
              this.closeD();
            }
          }, 1000);
        } else {
          // Assessment not yet started — show block dialog, do NOT close
          this.showMultiScreenBlockDialog = true;
        }
      } else {
        this.isMultiscreen = false;
        this.showMultiScreenDialog = false;
        this.showMultiScreenBlockDialog = false;
      }
    },

    retryScreenCheck() {
      if (window.screen.isExtended) {
        // Still extended — keep block dialog open
        this.showMultiScreenBlockDialog = true;
      } else {
        // Cleared — let them proceed
        this.isMultiscreen = false;
        this.showMultiScreenBlockDialog = false;
      }
    },

    stopMultiScreenCheck() {
      this.multiScreenCheckActive = false;
      this.showMultiScreenDialog = false;
      this.showMultiScreenBlockDialog = false;
      this.isMultiscreen = false;
      clearInterval(this.multiScreenCountdownInterval);
    },

    shuffleArray(array) {
      const shuffled = [...array];
      let i = shuffled.length;
      while (i !== 0) {
        const j = Math.floor(Math.random() * i--);
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },

    proceed() {
      localStorage.removeItem(process.env.VUE_APP_STORAGE_KEY);
      this.isWarningAccepted = true;
      this.showWarningDialog = false;
      this.startTimer();
    },

    startTimer() {
      clearInterval(this.timerInterval);
      let endTime = localStorage.getItem(process.env.VUE_APP_STORAGE_KEY);

      if (!endTime) {
        endTime = Date.now() + this.duration * 60 * 1000;
        localStorage.setItem(
          process.env.VUE_APP_STORAGE_KEY,
          endTime.toString(),
        );
      } else {
        endTime = Number(endTime);
      }

      this.isTimerRunning = true;
      this.timerInterval = setInterval(() => {
        const remaining = Math.floor((endTime - Date.now()) / 1000);
        if (remaining <= 0) {
          this.isTimerRunning = false;
          clearInterval(this.timerInterval);
          localStorage.removeItem(process.env.VUE_APP_STORAGE_KEY);
          this.timeRemaining = 0;
        } else {
          this.timeRemaining = remaining;
        }
      }, 1000);
    },

    nextCard() {
      if (this.currentIndex < this.questions.length - 1) {
        this.transitionDirection = "left";
        this.currentIndex++;
      }
    },

    prevCard() {
      if (this.currentIndex > 0) {
        this.transitionDirection = "right";
        this.currentIndex--;
      }
    },

    goToQuestion(idx) {
      this.transitionDirection = idx > this.currentIndex ? "left" : "right";
      this.currentIndex = idx;
    },

    jumpAndClose(idx) {
      if (this.isVoluntarySubmit) {
        this.cancelConfirm();
        this.goToQuestion(idx);
      }
    },

    submit() {
      this.isVoluntarySubmit = true;
      this.showAnswersDialog = true;
    },

    confirm() {
      this.showAnswersDialog = false;
      this.confirmBtnLoading = true;
      this.save();
    },

    cancelConfirm() {
      this.confirmBtnLoading = false;
      this.isVoluntarySubmit = false;
      this.showAnswersDialog = false;
    },

    async save() {
      if (this.questions.length > 0) {
        this.axiosCall("/skill-assessement/submitMySkillAssessment", "POST", {
          skill_assessment_id: this.data.id,
          questions: JSON.stringify(this.questions),
        }).then((res) => {
          if (res.data.status == 201) {
            this.fadeAwayMessage = {
              show: true,
              type: "success",
              header: "System Message",
              message: res.data.msg,
            };
            this.confirmBtnLoading = false;
            this.closeD();
          } else {
            this.fadeAwayMessage = {
              show: true,
              type: "error",
              header: "System Message",
              message: res.data.msg,
            };
            this.confirmBtnLoading = false;
          }
        });
      }
    },

    async saveOnAutoSubmission() {
      if (this.questions.length > 0) {
        this.axiosCall("/skill-assessement/submitMySkillAssessment", "POST", {
          skill_assessment_id: this.data.id,
          questions: JSON.stringify(this.questions),
        });
      }
    },

    handleVisibilityChange() {
      this.saveOnAutoSubmission();
      this.showDialogForSwitching = true;
    },

    reload() {
      location.reload();
    },

    closeD() {
      clearInterval(this.timerInterval);
      clearInterval(this.multiScreenCountdownInterval);
      this.isTimerRunning = false;
      this.isWarningAccepted = false;
      this.showWarningDialog = false;
      this.showAnswersDialog = false;
      this.stopMultiScreenCheck();
      eventBus.emit("closeStudentSkillAssessmentDialog", true);
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
/* ── Top bar ── */
.assessment-topbar {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.timer-chip {
  border: 1.5px solid;
  font-feature-settings: "tnum";
}
.timer-chip--normal {
  border-color: #d1d5db;
  color: #374151;
  background: #f9fafb;
}
.timer-chip--warning {
  border-color: #ef4444;
  color: #ef4444;
  background: #fef2f2;
  animation: pulse-timer 1s ease-in-out infinite;
}
@keyframes pulse-timer {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.65;
  }
}

/* ── Layout ── */
.assessment-body {
  height: calc(100vh - 65px);
}

.nav-side {
  width: 80px;
  flex-shrink: 0;
}

.question-center {
  height: 100%;
  overflow-y: auto;
}

/* ── Question card ── */
.option-radio :deep(.v-label) {
  font-size: 0.9rem;
  line-height: 1.5;
}

/* ── Pagination dots ── */
.pag-row {
  min-height: 36px;
}
.pag-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: transform 0.15s, background 0.15s;
  outline: none;
}
.pag-dot--current {
  width: 14px;
  height: 14px;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
}
.pag-dot--answered {
  background: #86efac;
}
.pag-dot--empty {
  background: #d1d5db;
}
.pag-dot:hover {
  transform: scale(1.3);
}

/* ── Answer grid ── */
.answer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 8px;
}
.ans-cell {
  padding: 10px 4px;
  border-radius: 6px;
  border: none;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.ans-cell--answered {
  background: #d1fae5;
  color: #065f46;
}
.ans-cell--empty {
  background: #f3f4f6;
  color: #6b7280;
}
.ans-cell:hover {
  opacity: 0.8;
}

.ans-legend {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
}
.ans-legend--answered {
  background: #d1fae5;
}
.ans-legend--empty {
  background: #f3f4f6;
}

/* ── Instructions dialog icon ── */
.instructions-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Slide transitions ── */
.slide-left-enter-from {
  transform: translateX(60px);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translateX(-60px);
  opacity: 0;
}
.slide-right-enter-from {
  transform: translateX(-60px);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(60px);
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
</style>
